import * as Yup from 'yup';

// Aligned with api-gateway's `UpdateUserProfileDto` (extends `BaseUserDto`):
//   firstName / lastName — @IsString @MaxLength(64), no character pattern
//   phone                — @Matches(locationFieldParameters.phone) @MaxLength(16)
// The DTO marks all three @IsOptional; this form keeps them required as a product decision — the
// fields are marked required in the UI and phone became mandatory in S20-5314. `required` is stricter
// than the backend but never rejects a value the backend would accept, so the two stay aligned.
const NAME_MAX = 64;

// Backend has NO character pattern on names, so we don't either: a stricter client rule would block
// accented or punctuated names (e.g. "José", "O'Brien-Smith Jr.") that already live in the DB and are
// valid on save, locking those users out of the form the moment they edit another field.
const nameRule = (field: string) =>
  Yup.string().trim().required(`${field} is required`).max(NAME_MAX, `${field} must be at most ${NAME_MAX} characters`);

// Phone rule replicated verbatim from the gateway's `locationFieldParameters.phone.match.pattern`:
// E.164 (`+11234567890`) or the legacy `(123) 456-7890`. The phone input emits E.164 and legacy values
// are normalized on open (see normalizePhone), so in practice only the E.164 branch is exercised — the
// legacy branch is kept solely to stay aligned with what the gateway accepts.
const PHONE_REGEX = /^(?:\+[1-9]\d{1,14}|\(\d{3}\) \d{3}-\d{4})$/i;
const PHONE_MESSAGE = 'Enter a valid phone number';
const PHONE_MAX = 16;

/**
 * Validation for the General tab's editable fields. Email and location are read-only on this page, so
 * they are not part of the schema — only `{firstName, lastName, phone}` are submitted. Kept in step
 * with api-gateway's `BaseUserDto` so the client never accepts what the backend rejects (or vice versa).
 */
export const validationSchema = Yup.object({
  firstName: nameRule('First name'),
  lastName: nameRule('Last name'),
  phone: Yup.string()
    .required('Phone is required')
    .max(PHONE_MAX, `Phone must be at most ${PHONE_MAX} characters`)
    .matches(PHONE_REGEX, PHONE_MESSAGE),
});
